/**
 * 电站 deviceFlag 位标记
 *
 * 后端用一个整数的各个 bit 位表达电站能力，deviceFlag = 各位 OR 之和。
 * 例如 储能 + 光伏 + 充电桩 = 0b111 = 7。
 *
 * 注意：历史代码里 `(Number(flag) & (1 << bit)) !== 0` 这个表达式在
 * station-add/index.vue、workbench/approve/index.vue、StationAddStepDevice.vue
 * 等处各写了一遍，且「设备配置」下拉的 value 存的是 bit 序号而非掩码（bit4 变压器
 * 的 value 是 4，掩码却是 16，极易看错）。本文件是新的单一真源，
 * 后续可逐步把那些内联位运算收敛过来。
 */

/** deviceFlag 各 bit 位含义 */
export const DEVICE_FLAG_BITS = {
  /** bit0 储能，固定为 1 */
  ESS: 0,
  /** bit1 光伏 */
  PV: 1,
  /** bit2 充电桩 */
  CHARGE_PILE: 2,
  // bit3 预留未使用
  /** bit4 变压器 */
  TRANSFORMER: 4,
  /** bit5 按间隔统计电量（决定现货结算、按间隔统计电量等功能是否开放） */
  INTERVAL_ENERGY: 5,
}

/**
 * 表单「设备配置」里可勾选的 bit 位（bit0 储能固定必选，不在可选项内）。
 * 组装 / 解析 / 校验三处共用，避免各写一份 [1,2,4] 魔法数组而遗漏新增位。
 */
export const SELECTABLE_DEVICE_FLAG_BITS = [
  DEVICE_FLAG_BITS.PV,
  DEVICE_FLAG_BITS.CHARGE_PILE,
  DEVICE_FLAG_BITS.TRANSFORMER,
  DEVICE_FLAG_BITS.INTERVAL_ENERGY,
]

/**
 * 判断 deviceFlag 上某个 bit 位是否置位
 * @param {number|string|null|undefined} deviceFlag 后端下发的位标记
 * @param {number} bit bit 序号，取自 DEVICE_FLAG_BITS
 * @returns {boolean} 非法入参一律返回 false
 */
export function hasDeviceFlag(deviceFlag, bit) {
  const n = Number(deviceFlag)
  if (!Number.isFinite(n)) return false
  return (n & (1 << bit)) !== 0
}

/**
 * 后端 deviceFlag → 表单勾选的 bit 序号数组
 * @param {number|string|null|undefined} deviceFlag
 * @returns {number[]} 已置位的可选 bit 序号
 */
export function parseDeviceFlags(deviceFlag) {
  return SELECTABLE_DEVICE_FLAG_BITS.filter((bit) => hasDeviceFlag(deviceFlag, bit))
}

/**
 * 表单勾选的 bit 序号数组 → 后端 deviceFlag
 * bit0（储能）固定置位，故初始值为 1
 * @param {number[]} bits
 * @returns {number}
 */
export function buildDeviceFlag(bits) {
  return (bits || []).reduce((acc, bit) => {
    const n = Number(bit)
    if (!Number.isInteger(n) || !SELECTABLE_DEVICE_FLAG_BITS.includes(n)) return acc
    return acc | (1 << n)
  }, 1)
}
